import { useState, type Dispatch } from "react";
import { useTranslation } from "react-i18next";
import type { Review } from "../../../api/content/reviewDecoders";
import ErrorNotice from "../../../errors/ErrorNotice";
import { useSubmission } from "../../../shared/state/useSubmission";
import AppButton from "../../../ui/buttons/AppButton";
import { classNames } from "../../../ui/styles/classNames";
import { actionAvailable, curatesNone, offeredActions, primaryAction, servedAction, type DecisionAction } from "../state/decisionRules";
import { blockingCount, type ReviewPageEvent, type ReviewPageState } from "../state/reviewPageState";
import { ACTION_LABEL_KEYS } from "./decisionLabels";
import DecisionForm from "./DecisionForm";
import Chip from "../../../ui/feedback/Chip";

/**
 * The decision, kept in view at the top of the review (UX-25, curation-ui.md CU-7): what
 * still blocks a merge, "next open item", and the caller's actions. An action opens a short
 * form below the bar for its comment, and for a merge the acknowledgement, before it is sent.
 */
export default function DecisionBar({
  review,
  state,
  dispatch,
  openCount,
  unmarked,
  prefill,
  requested,
  onNextOpen,
  onChanged,
}: {
  review: Review;
  state: ReviewPageState;
  dispatch: Dispatch<ReviewPageEvent>;
  /** Open decisions, as v1's counter (curation-ui.md CU-2). */
  openCount: number;
  unmarked: number;
  prefill: string;
  /** An action asked for by key (`a`, `r`, `m`), with a count so the same key opens it again. */
  requested: { readonly action: DecisionAction; readonly count: number } | null;
  onNextOpen: () => void;
  onChanged: () => void;
}) {
  const { t } = useTranslation();
  const submission = useSubmission();
  const actions = offeredActions(review);
  const [pending, setPending] = useState<DecisionAction | null>(null);
  // A key asks for an action; open its form once per request (state adjusted while rendering).
  const [answered, setAnswered] = useState(requested);
  if (answered !== requested) {
    setAnswered(requested);
    if (requested !== null && actions.includes(requested.action) && actionAvailable(requested.action, review, state)) {
      setPending(requested.action);
    }
  }
  const changeSet = review.changeSet;
  if (changeSet.state === "merged" || changeSet.state === "scrapped") {
    return <p className="app-text-muted text-sm">{t(`reviews.summary.closed.${changeSet.state}`)}</p>;
  }
  const primary = primaryAction(actions, review, state);
  const blocking = blockingCount(review);
  const open = openCount;
  const busy = submission.state.status === "submitting";
  const uncovered = review.summary.uncovered.length;
  // Merge says the reason its command refuses with first (module B's order).
  const mergeRefusal = servedAction(review, "merge")?.refusal;
  const mergeReason =
    mergeRefusal === "merge_blocked" ? t("reviews.mergeBlocks.explanation") : mergeRefusal === "approvals_missing" ? t("reviews.merge.waiting", { count: uncovered }) : undefined;

  return (
    <section className="app-card sticky top-0 z-10 space-y-2 p-3 shadow-md" aria-label={t("reviews.decisions.barLabel")}>
      <div className="flex flex-wrap items-center gap-2">
        <p className="min-w-0 basis-full text-sm xl:basis-0 xl:flex-1" role="status">
          <span className={classNames("font-semibold", review.mergeBlocks.blocked || blocking > 0 ? "app-status-text-error" : "app-status-text-success")}>
            {review.mergeBlocks.blocked ? t("reviews.mergeBlocks.title") : blocking > 0 ? t("reviews.summary.blocking", { count: blocking }) : t("reviews.summary.nothingBlocking")}
          </span>
          {uncovered > 0 ? <span className="app-text-muted">. {t("reviews.summary.approvalsMissing", { count: uncovered })}</span> : null}
          <Chip tone={open > 0 ? "warning" : "success"} className="ml-2 whitespace-nowrap">{t("reviews.curation.openDecisions", { count: open })}</Chip>
        </p>
        <div className="ml-auto flex flex-wrap justify-end gap-2">
          {/* The one way to the next open decision (OW-10); `n` does the same. */}
          <AppButton size="sm" variant="ghost" disabled={open === 0} aria-keyshortcuts="n" title={t("reviews.summary.openItems", { count: open })} onClick={onNextOpen}>
            {t("reviews.curation.nextUnresolved")}
          </AppButton>
          {actions.map((action) => (
            <AppButton
              key={action}
              size="sm"
              variant={action === primary ? "primary" : action === "scrap" ? "ghost" : "secondary"}
              className={action === "scrap" ? "app-status-text-error" : undefined}
              aria-expanded={pending === action}
              disabled={busy || !actionAvailable(action, review, state)}
              title={action === "merge" ? mergeReason : undefined}
              onClick={() => {
                setPending(pending === action ? null : action);
              }}
            >
              {t(ACTION_LABEL_KEYS[action])}
            </AppButton>
          ))}
        </div>
      </div>
      {curatesNone(review) ? <p className="app-text-muted text-sm">{t("reviews.decisions.notCurator")}</p> : null}
      {pending !== null && actions.includes(pending) && actionAvailable(pending, review, state) ? (
        <DecisionForm
          key={`${pending}:${review.head}`}
          action={pending}
          review={review}
          state={state}
          dispatch={dispatch}
          submission={submission}
          unmarked={unmarked}
          prefill={prefill}
          onCancel={() => {
            setPending(null);
          }}
          onSent={() => {
            setPending(null);
            onChanged();
          }}
        />
      ) : null}
      {submission.state.status === "failed" ? <ErrorNotice error={submission.state.error} onRetry={onChanged} compact={true} /> : null}
    </section>
  );
}

